import { CalendarCheck, MapPin, Plus } from 'lucide-react';
import { Button } from '~/components/ui/button';

export interface ScheduleGridEntry {
  date: string;
  startsAt: string;
  endsAt: string;
  timeLabel: string;
  title: string;
  classroom?: { id: string; name: string } | null;
  classOffering?: { id: string; displayName: string } | null;
  scheduleBlock: { id: string; attendanceEnabled: boolean };
}

interface Props {
  dates: ReadonlyArray<string>;
  entries: ReadonlyArray<ScheduleGridEntry>;
  onAdd: (date: string, startsAt: string, endsAt: string) => void;
  onAttendance: (blockId: string, date: string) => void;
  onSelect?: (blockId: string) => void;
  formatDate: (date: string) => string;
}

const SLOT_MINUTES = 30;
const DEFAULT_START_MINUTES = 7 * 60;
const DEFAULT_END_MINUTES = 17 * 60;

function timeToMinutes(value: string) {
  const [hours, minutes] = value.slice(0, 5).split(':').map(Number);
  return (hours * 60) + minutes;
}

function minutesToTime(value: number) {
  const bounded = Math.max(0, Math.min(value, (24 * 60) - 1));
  return `${String(Math.floor(bounded / 60)).padStart(2, '0')}:${String(bounded % 60).padStart(2, '0')}`;
}

function floorToSlot(value: number) {
  return Math.floor(value / SLOT_MINUTES) * SLOT_MINUTES;
}

function ceilToSlot(value: number) {
  return Math.ceil(value / SLOT_MINUTES) * SLOT_MINUTES;
}

function timeLabel(value: string) {
  const [hour, minute] = value.split(':').map(Number);
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })
    .format(new Date(2000, 0, 1, hour, minute));
}

export function scheduleGridSlots(entries: ReadonlyArray<ScheduleGridEntry>) {
  const earliestEntry = entries.length > 0
    ? Math.min(...entries.map((entry) => timeToMinutes(entry.startsAt)))
    : DEFAULT_START_MINUTES;
  const latestEntry = entries.length > 0
    ? Math.max(...entries.map((entry) => timeToMinutes(entry.endsAt)))
    : DEFAULT_END_MINUTES;
  const start = Math.max(0, Math.min(DEFAULT_START_MINUTES, floorToSlot(earliestEntry)));
  const end = Math.min(24 * 60, Math.max(DEFAULT_END_MINUTES, ceilToSlot(latestEntry)));
  const slots: string[] = [];

  for (let minute = start; minute < end; minute += SLOT_MINUTES) {
    slots.push(minutesToTime(minute));
  }

  return slots;
}

export function slotEnd(startsAt: string) {
  return minutesToTime(timeToMinutes(startsAt) + SLOT_MINUTES);
}

export default function ScheduleTimeGrid({ dates, entries, onAdd, onAttendance, onSelect, formatDate }: Props) {
  const slots = scheduleGridSlots(entries);
  const slotMinutes = slots.map(timeToMinutes);

  return (
    <div className="schedule-time-grid overflow-x-auto" role="grid" aria-label={`${dates.length === 1 ? 'Day' : 'Week'} schedule time grid`}>
      <div
        className="grid min-w-[44rem] gap-px overflow-hidden rounded-t-md border bg-border"
        style={{ gridTemplateColumns: `5.5rem repeat(${Math.max(dates.length, 1)}, minmax(9rem, 1fr))` }}
      >
        <div role="columnheader" className="bg-muted p-2 text-xs font-semibold uppercase text-muted-foreground">Time</div>
        {dates.map((date) => (
          <div key={date} role="columnheader" className="bg-muted p-2 text-sm font-semibold">{formatDate(date)}</div>
        ))}

      </div>
      <div
        className="grid min-w-[44rem] gap-px overflow-hidden rounded-b-md border-x border-b bg-border"
        style={{ gridTemplateColumns: `5.5rem repeat(${Math.max(dates.length, 1)}, minmax(9rem, 1fr))` }}
      >
        <div className="grid gap-px bg-border" style={{ gridTemplateRows: `repeat(${slots.length}, 4.5rem)` }}>
          {slots.map((startsAt) => (
            <div key={startsAt} role="rowheader" className="bg-background px-2 py-2 text-right text-xs text-muted-foreground">
              {timeLabel(startsAt)}
            </div>
          ))}
        </div>
        {dates.map((date) => {
          const dayEntries = entries
            .filter((entry) => String(entry.date) === date)
            .sort((first, second) => `${first.startsAt}-${first.endsAt}-${first.title}`.localeCompare(`${second.startsAt}-${second.endsAt}-${second.title}`));
          const accessibleDate = formatDate(date);

          return (
            <div key={date} role="rowgroup" className="grid gap-px bg-border" style={{ gridTemplateRows: `repeat(${slots.length}, 4.5rem)`, gridTemplateColumns: 'minmax(0, 1fr)' }}>
              {slots.map((startsAt, index) => {
                const startMinute = slotMinutes[index];
                const endMinute = startMinute + SLOT_MINUTES;
                const occupied = dayEntries.some((entry) => timeToMinutes(entry.startsAt) < endMinute && timeToMinutes(entry.endsAt) > startMinute);
                const endsAt = slotEnd(startsAt);
                return (
                  <div key={startsAt} role="gridcell" className="relative bg-background" style={{ gridColumn: 1, gridRow: index + 1 }}>
                    <button
                      type="button"
                      className="absolute inset-0 z-0 rounded-sm text-transparent outline-none transition-colors hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
                      aria-label={`Add block on ${accessibleDate} at ${timeLabel(startsAt)}`}
                      onClick={() => onAdd(date, startsAt, endsAt)}
                    >
                      Add block
                    </button>
                    {occupied && (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="absolute right-1 top-1 z-30 h-6 w-6 bg-background/90"
                        aria-label={`Add overlapping block on ${accessibleDate} at ${timeLabel(startsAt)}`}
                        onClick={() => onAdd(date, startsAt, endsAt)}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                );
              })}
              {dayEntries.map((entry) => {
                const entryStart = floorToSlot(timeToMinutes(entry.startsAt));
                const startIndex = Math.max(0, slotMinutes.indexOf(entryStart));
                const duration = Math.max(1, Math.ceil((timeToMinutes(entry.endsAt) - entryStart) / SLOT_MINUTES));
                const overlaps = dayEntries.filter((other) => (
                  timeToMinutes(other.startsAt) < timeToMinutes(entry.endsAt) &&
                  timeToMinutes(other.endsAt) > timeToMinutes(entry.startsAt)
                ));
                const overlapIndex = Math.max(0, overlaps.indexOf(entry));
                const width = 100 / Math.max(overlaps.length, 1);

                return (
                  <article
                    key={`${entry.scheduleBlock.id}-${entry.date}-${entry.startsAt}`}
                    className="relative z-10 m-1 min-w-0 overflow-hidden rounded border bg-card p-2 pr-8 shadow-sm"
                    style={{
                      gridColumn: 1,
                      gridRow: `${startIndex + 1} / span ${Math.min(duration, slots.length - startIndex)}`,
                      width: `calc(${width}% - 0.5rem)`,
                      marginLeft: `calc(${overlapIndex * width}% + 0.25rem)`,
                    }}
                  >
                    {onSelect && (
                      <button
                        type="button"
                        className="absolute inset-0 z-0 rounded text-transparent outline-none transition-colors hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
                        aria-label={`Edit ${entry.title}`}
                        onClick={() => onSelect(entry.scheduleBlock.id)}
                      >
                        Edit block
                      </button>
                    )}
                    <p className="pointer-events-none text-[11px] font-medium text-muted-foreground">{entry.timeLabel}</p>
                    <p className="pointer-events-none truncate text-sm font-semibold leading-tight" title={entry.title}>{entry.title}</p>
                    {entry.classOffering && entry.title !== entry.classOffering.displayName && (
                      <p className="pointer-events-none truncate text-xs text-muted-foreground" title={entry.classOffering.displayName}>{entry.classOffering.displayName}</p>
                    )}
                    <p className="pointer-events-none mt-1 flex items-center gap-1 truncate text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3 shrink-0" /> {entry.classroom?.name ?? 'Location not assigned'}
                    </p>
                    {entry.scheduleBlock.attendanceEnabled && (
                      <Button
                        size="icon"
                        variant="outline"
                        className="absolute bottom-1 right-1 z-20 h-6 w-6"
                        aria-label="Attendance"
                        onClick={() => onAttendance(entry.scheduleBlock.id, date)}
                      >
                        <CalendarCheck className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </article>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
